import axios from "axios";
import router from "@/router";
import i18n from "@/i18n";
import {
  useConsoleLog as log,
  useConsoleError as logErr,
  useConsoleXhrOut as logXhrOut,
  useConsoleXhrIn as logXhrIn
} from "@/composables/console.js";

import { useRoute } from "vue-router";
import { useUserStore } from "@/stores/user";
import { useAppStore } from "@/stores/app";

export default class useComm {
  static uploadController = new AbortController();
  static logApiCalls = false;

  static get({
    path,
    data = {},
    ignoreErrorCodes = [],
    mockData = null,
    fullResponseObject = false,
    responseType = null
  }) {
    return this.request({
      method: "get",
      path,
      data,
      ignoreErrorCodes,
      mockData,
      fullResponseObject,
      responseType
    });
  }

  static post({
    path,
    data = {},
    ignoreErrorCodes = [],
    mockData = null,
    fullResponseObject = false
  }) {
    return this.request({
      method: "post",
      path,
      data,
      ignoreErrorCodes,
      mockData,
      fullResponseObject
    });
  }

  static put({
    path,
    data = {},
    ignoreErrorCodes = [],
    mockData = null,
    fullResponseObject = false
  }) {
    return this.request({
      method: "put",
      path,
      data,
      ignoreErrorCodes,
      mockData,
      fullResponseObject
    });
  }

  static patch({
    path,
    data = {},
    ignoreErrorCodes = [],
    mockData = null,
    fullResponseObject = false
  }) {
    return this.request({
      method: "patch",
      path,
      data,
      ignoreErrorCodes,
      mockData,
      fullResponseObject
    });
  }

  static delete({
    path,
    data = {},
    ignoreErrorCodes = [],
    mockData = null,
    fullResponseObject = false
  }) {
    return this.request({
      method: "delete",
      path,
      data,
      ignoreErrorCodes,
      mockData,
      fullResponseObject
    });
  }

  static async request({
    method,
    path,
    data,
    ignoreErrorCodes = [],
    mockData = null,
    fullResponseObject = false,
    responseType = null
  }) {
    // Handle mock data
    if (mockData) {
      return mockData;
    }

    const url = path.startsWith("http")
      ? path
      : import.meta.env.VITE_API_URL + path;

    if (this.logApiCalls) logXhrOut(path, method, data);

    const user = useUserStore();

    let headers = {};
    let authToken = null;
    if (useAppStore().mode == "embedded") authToken = useAppStore().apiKey;
    else if (user.token) authToken = user.token;
    if (authToken) {
      headers = {
        Authorization: "Bearer " + authToken
      };
    }

    let options = { method, url, data, headers };
    if (method == "get") options.params = data;
    if (responseType) options.responseType = responseType;

    try {
      const response = await axios(options);

      if (this.logApiCalls) logXhrIn(path, response);

      return fullResponseObject ? response : response.data;
    } catch (err) {
      logErr(err);

      // Our API sometimes has cold starts that may throw a timeout, retry
      // the request in that case
      if (err.code == "ECONNABORTED") {
        log("Request timeout reached, retrying...");
        return this.request({
          method,
          path,
          data,
          ignoreErrorCodes,
          mockData,
          fullResponseObject
        });
      }

      const status = err.response?.status;

      // First thing to check is if the error is returned by the /auth/refresh
      // endpoint. Any error on a call to this endpoint means we have to
      // authenticate the user again.
      if (path == "/auth/refresh") {
        const currentPath = useRoute()?.path;
        if (currentPath)
          localStorage.setItem("redirectAfterLogin", currentPath);

        logErr(
          `Token refresh request responded with ${status}: back to login.`
        );
        logErr("Setting routeWhenLogin to", currentPath);
        user.clear();
        user.initCompleted = true;
        router.push("/login");

        throw err;
      }

      // Apply default error handling (for those errors that are not explicitly skipped via ignoreErrorCodes)
      if (!ignoreErrorCodes.includes(status)) {
        // 401 Unauthorized. Token has expired. We'll try to refresh it, but
        // only if we are not requesting the refresh endpoint (to avoid an
        // infinite loop).
        if (status == 401 && !path.includes("/auth/refresh")) {
          logErr(`Server responded with ${status}: trying to refresh token.`);

          await this.refreshToken();
          // Token refreshed successfully, retry the original request
          return this.request({
            method,
            path,
            data,
            ignoreErrorCodes
          });
        }

        // 402 Payment required. We will silently ignore this one: the UI
        // already hides any section that requires a paid feature.
        if (status == 402) {
          return;
        }

        // 403 Forbidden. User doesn't have permission to perform this action.
        if (status == 403) {
          logErr(
            `Server responded with ${status}: user doesn't have permission to perform this action.`
          );
          ElMessage.error({
            message: i18n.global.t("loginView.noPermission"),
            customClass: "elMessageError"
          });
        }

        // 409 Conflict. Current token can't be used to obtain a new one. We
        // must go back to the login process.
        if (status == 409 && path.includes("/auth/refresh")) {
          const currentPath = useRoute()?.path;
          if (currentPath)
            localStorage.setItem("redirectAfterLogin", currentPath);

          logErr(`Server responded with ${status}: back to login.`);
          logErr("Setting routeWhenLogin to", currentPath);
          router.push("/login");

          throw err;
        } else {
          let detail = err.response?.data?.detail;
          if (detail) {
            if (!Array.isArray(detail)) detail = [{ msg: detail }];
            const details = detail?.map(d => d.msg).join("\n");
            ElMessage.error({
              message: details,
              customClass: "elMessageError",
              duration: 6000
            });
          }
        }
      }

      // Throw the error so the caller can handle it
      throw err;
    }
  }

  // This method is used to retrieve all data from an endpoint. It will call
  // the method with the max number of results as many times as needed until
  // all items are retrieved. Please use with caution.
  static async harvest({ method = "get", path, data = {}, items = 100 }) {
    const collectedData = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const currentPath = `${path}?page=${page}&items=${items}`;

      const res = await this.request({
        method,
        path: currentPath,
        data
      });

      collectedData.push(...res.data);

      // Check if we need to fetch more pages
      if (res.navigation.total_elements > collectedData.length) {
        page++;
      } else {
        hasMore = false;
      }
    }

    return collectedData;
  }

  static async uploadFileToS3({ file, urls, cbProgress, cbSpeed }) {
    const etags = [];

    const chunkSize = Math.ceil(file.size / urls.length);
    log(`Chunk size is ${chunkSize} bytes`);
    let totalUploaded = 0;

    // Speed calculation variables
    let uploadStartTime = Date.now();
    let lastSpeedUpdate = uploadStartTime;
    let lastUploadedBytes = 0;
    const speedUpdateInterval = 1000; // Update speed every 1 second

    for (let i = 0; i < urls.length; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);

      try {
        const res = await axios.put(urls[i], blob, {
          headers: { "Content-Type": false },
          signal: this.uploadController.signal,
          onUploadProgress: progressEvent => {
            const currentTotalUploaded = totalUploaded + progressEvent.loaded;
            const totalProgress = (currentTotalUploaded / file.size) * 100;

            if (cbProgress) cbProgress(totalProgress);

            // Calculate and report upload speed
            if (cbSpeed) {
              const now = Date.now();
              const timeSinceLastUpdate = now - lastSpeedUpdate;

              if (timeSinceLastUpdate >= speedUpdateInterval) {
                const bytesSinceLastUpdate =
                  currentTotalUploaded - lastUploadedBytes;
                const speedBytesPerSecond =
                  bytesSinceLastUpdate / (timeSinceLastUpdate / 1000);
                const speedMBPerSecond = speedBytesPerSecond / (1024 * 1024);

                // Calculate ETA
                const remainingBytes = file.size - currentTotalUploaded;
                const etaSeconds =
                  speedBytesPerSecond > 0
                    ? remainingBytes / speedBytesPerSecond
                    : 0;

                cbSpeed({
                  speedMBps: speedMBPerSecond,
                  etaSeconds: etaSeconds
                });

                lastSpeedUpdate = now;
                lastUploadedBytes = currentTotalUploaded;
              }
            }
          }
        });

        etags.push(JSON.parse(res.headers.etag));
        log(`Uploaded part ${i + 1}`);
        totalUploaded += blob.size;
      } catch (error) {
        logErr(error);

        if (axios.isCancel(error)) {
          const abortError = new Error("Upload aborted");
          abortError.isAbort = true;
          abortError.originalError = error;
          throw abortError;
        }

        throw error;
      }
    }

    log("File uploaded successfully.");
    return etags;
  }

  static abortUpload() {
    this.uploadController.abort();
    this.uploadController = new AbortController();
  }

  static async refreshToken() {
    log("Refreshing token");

    const res = await this.post({
      path: "/auth/refresh"
    });

    const user = useUserStore();
    user.saveToken(res.data.access_token);
    log("Token refreshed");

    return res;
  }
}
